import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import NotificationCart from "./NotificationCart";
import apiClient from "../api/ResturantClient";
import SetupProgressBar from "./SetupProgressBar";

const MenuManagement = () => {
  const restaurant_id = sessionStorage.getItem("restaurant_id");
  const [menuItems, setMenuItems] = useState([]);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationStatus, setNotificationStatus] = useState("");
  const [isNotification, setIsNotification] = useState(false);
  const [isFormComplete, setIsFormComplete] = useState(false);
  const [formData, setFormData] = useState({
    item_name: "",
    description: "",
    price: "",
    category: "",
    image: "",
    available: true,
  });
  const fileInputRef = useRef(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSetupProgress, setShowSetupProgress] = useState(() => {
    return (
      localStorage.getItem(`restaurant_${restaurant_id}_menuSetup`) ===
      "pending"
    );
  });
  // New state for image suggestion modal
  const [showImageSuggestions, setShowImageSuggestions] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    fetchMenuItems();
  }, []);

  useEffect(() => {
    const requiredFields = ["item_name", "description", "price", "category"];

    const allFieldsFilled = requiredFields.every((field) => {
      if (field === "price") {
        return (
          formData[field] !== "" &&
          !isNaN(formData[field]) &&
          parseFloat(formData[field]) >= 0
        );
      }
      return formData[field] && formData[field].toString().trim() !== "";
    });

    setIsFormComplete(allFieldsFilled);
  }, [formData]);

  const showNotification = (message, status) => {
    setNotificationMessage(message);
    setNotificationStatus(status);
    setIsNotification(true);
    setTimeout(() => {
      setIsNotification(false);
    }, 9000);
  };

  const [buttonClicked, setButtonClicked] = useState(false);
  const [showEnableButton, setShowEnableButton] = useState(false);

  const fetchMenuItems = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(
        `/menu/get-menu-items/${restaurant_id}`
      );
      setMenuItems(response.data);
      const menuSetupStatus = localStorage.getItem(
        `restaurant_${restaurant_id}_menuSetup`
      );
      setShowEnableButton(
        response.data.length > 0 && menuSetupStatus === "pending"
      );
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleEnableDashboard = () => {
    localStorage.setItem(`restaurant_${restaurant_id}_menuSetup`, "completed");
    setShowSetupProgress(false);
    setButtonClicked(true);
    window.location.reload();
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (name === "price") {
      const numValue = Number(value);
      if (numValue < 0) return;

      setFormData({
        ...formData,
        price: numValue || "",
      });
      return;
    }

    setFormData({
      ...formData,
      [name]:
        type === "checkbox" ? checked : type === "file" ? files[0] : value,
    });

    if (name === "image") {
      setImagePreview(URL.createObjectURL(files[0]));
    }
  };

  const handleImageUpload = async (imageFile) => {
    try {
      const fileName = encodeURIComponent(imageFile.name);
      const response = await apiClient.post(`/menu/generate-signed-url`, {
        file_name: fileName,
      });
      const signedUrl = response.data.signed_url;
      await axios.put(signedUrl, imageFile, {
        headers: {
          "Content-Type": imageFile.type,
          "x-amz-acl": "public-read",
        },
      });

      return signedUrl.split("?")[0];
    } catch (error) {
      console.log("Error uploading image:", error);

      throw new Error("Image upload failed");
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setShowEnableButton(false);
    try {
      let imageUrl = formData.image;

      if (formData.image instanceof File) {
        imageUrl = await handleImageUpload(formData.image);
      }

      const formDataToSend = {
        ...formData,
        image: imageUrl,
        restaurant_id,
      };

      const endpoint = formData.menu_id
        ? `/menu/update-menu-item/${formData.menu_id}`
        : `/menu/add-menu-item`;

      await apiClient.post(endpoint, formDataToSend);

      setFormData({
        item_name: "",
        description: "",
        price: "",
        category: "",
        image: "",
        available: true,
      });
      setImagePreview(null);
      if (fileInputRef.current) fileInputRef.current.value = "";

      showNotification(
        formData.menu_id
          ? "Menu item updated successfully"
          : "Menu item added successfully",
        "success"
      );
      setShowEnableButton(true);
    } catch (error) {
      showNotification("Error adding/updating menu item", "error");
    } finally {
      await fetchMenuItems();
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (menu_id) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      try {
        await apiClient.delete(`/menu/delete-menu-item/${menu_id}`);
        setMenuItems(menuItems.filter((item) => item.menu_id !== menu_id));
        showNotification("Menu item deleted successfully", "success");
      } catch (error) {
        showNotification("Error deleting menu item", "error");
      }
    }
  };

  const handleEdit = (item) => {
    setFormData({
      menu_id: item.menu_id,
      item_name: item.item_name,
      description: item.description,
      price: parseFloat(item.price),
      category: item.category,
      available: item.available,
      image: item.image,
    });
    setImagePreview(item.image);

    // Scroll to the form section
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-4 px-4">
      <div className="max-w-6xl mx-auto">
        {showSetupProgress && (
          <SetupProgressBar currentStep={menuItems.length > 0 ? 3 : 2} />
        )}
        
        {/* Header Section */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl shadow-xl p-2 mb-4 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">Menu Management</h2>
          <p className="text-blue-100 text-lg">
            Create and manage your restaurant menu items
          </p>
        </div>

        {isNotification && (
          <NotificationCart
            message={notificationMessage}
            status={notificationStatus}
          />
        )}

        {!loading && showEnableButton && !buttonClicked && (
          <button
            onClick={handleEnableDashboard}
            className="w-full max-w-6xl mx-auto mb-8 py-4 px-6 
          bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 
          text-white font-bold rounded-xl shadow-lg transform hover:scale-105
          transition-all duration-300 ease-in-out"
          >
            Complete Setup & Enable Dashboard
          </button>
        )}

        {/* Form Section */}
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="bg-white rounded-2xl shadow-xl p-8 mb-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Item Name
                </label>
                <input
                  type="text"
                  name="item_name"
                  value={formData.item_name}
                  onChange={handleChange}
                  required
                  maxLength={50}
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                  placeholder="Enter item name (max 50 characters)"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  maxLength={200}
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 h-32"
                  placeholder="Enter item description (max 200 characters)"
                />
              </div>
            </div>

            <div className="space-y-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Price
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  min="0"
                  step="any"
                  required
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                  placeholder="Enter price"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Category
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white"
                >
                  <option value="">Select a category</option>
                  <option value="Starter">Starter</option>
                  <option value="MainCourse">Main Course</option>
                  <option value="Dessert">Dessert</option>
                  <option value="Beverage">Beverage</option>
                  <option value="TodaysSpecial">Today's Special</option>
                </select>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Image
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowImageSuggestions(true)}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Need image suggestions?
                  </button>
                </div>
                <input
                  type="file"
                  name="image"
                  onChange={handleChange}
                  accept="image/*"
                  ref={fileInputRef}
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                />
              </div>

              {imagePreview && (
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Image Preview
                  </label>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-48 object-cover rounded-xl border-2 border-gray-200"
                  />
                </div>
              )}

              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                <input
                  type="checkbox"
                  name="available"
                  checked={formData.available}
                  onChange={handleChange}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-5 w-5"
                />
                <label className="text-sm font-semibold text-gray-700">
                  Available for ordering
                </label>
              </div>
            </div>
          </div>

          {/* Image Suggestions Modal */}
          {showImageSuggestions && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-gray-800">
                    Find Food Images
                  </h3>
                  <button
                    onClick={() => setShowImageSuggestions(false)}
                    className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                  Search for high-quality food images on these free stock photo websites:
                </p>
                <div className="space-y-4">
                  {[
                    {
                      name: "Pexels",
                      url: `https://www.pexels.com/search/${encodeURIComponent(
                        formData.item_name || "food"
                      )}/`,
                      color: "blue",
                      icon: (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      )
                    },
                    {
                      name: "Unsplash",
                      url: `https://unsplash.com/s/photos/${encodeURIComponent(
                        formData.item_name || "food"
                      )}`,
                      color: "gray",
                      icon: (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      )
                    },
                    {
                      name: "Pixabay",
                      url: `https://pixabay.com/images/search/${encodeURIComponent(
                        formData.item_name || "food"
                      )}/`,
                      color: "green",
                      icon: (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      )
                    }
                  ].map((site) => (
                    <a
                      key={site.name}
                      href={site.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-all duration-200 border border-gray-200 hover:border-gray-300"
                      onClick={() => setShowImageSuggestions(false)}
                    >
                      <div className="flex items-center">
                        <div className={`w-10 h-10 bg-${site.color}-100 rounded-lg flex items-center justify-center mr-4`}>
                          {site.icon}
                        </div>
                        <span className="font-semibold text-gray-800">{site.name}</span>
                      </div>
                    </a>
                  ))}
                </div>
                <div className="mt-6 text-sm text-gray-500 bg-blue-50 p-4 rounded-lg">
                  <p>
                    After finding an image, download it to your device and upload it using the file input above.
                  </p>
                </div>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting || !isFormComplete}
            className={`w-full mt-8 py-4 px-6 rounded-xl font-bold text-lg transition-all duration-300 ${
              isSubmitting
                ? "bg-gray-400 cursor-not-allowed"
                : isFormComplete
                ? "bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            {isSubmitting
              ? "Processing..."
              : formData.menu_id
              ? "Update Menu Item"
              : "Add Menu Item"}
          </button>
        </form>

        {/* Menu Items Grid */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500 mb-4"></div>
            <p className="text-gray-600 text-lg font-semibold">Loading menu items...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {menuItems.map((item) => (
              <div
                key={item.menu_id}
                className="bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col h-full transform hover:scale-105 transition-all duration-300"
              >
                {/* Image Section */}
                <div className="h-48 overflow-hidden">
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.item_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                      <span className="text-gray-400 font-medium">No Image</span>
                    </div>
                  )}
                </div>

                {/* Content Section */}
                <div className="p-6 flex flex-col flex-grow">
                  <div className="flex-grow">
                    <h3 className="text-xl font-bold text-gray-800 line-clamp-1 mb-2">
                      {item.item_name}
                    </h3>
                    <p className="text-gray-600 line-clamp-2 mb-4">
                      {item.description}
                    </p>
                    <div className="flex justify-between items-center mb-4">
                      <p className="text-2xl font-bold text-blue-600">
                        ${item.price.toFixed(2)}
                      </p>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        item.available 
                          ? "bg-green-100 text-green-800" 
                          : "bg-red-100 text-red-800"
                      }`}>
                        {item.available ? "Available" : "Unavailable"}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 bg-gray-50 p-2 rounded-lg">
                      Category: <span className="font-semibold">{item.category}</span>
                    </p>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3 mt-6 pt-4 border-t border-gray-100">
                    <button
                      onClick={() => handleEdit(item)}
                      className="flex-1 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(item.menu_id)}
                      className="flex-1 py-3 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-xl font-semibold hover:from-red-600 hover:to-pink-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MenuManagement;